import React from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Legend,
  Tooltip,
} from "chart.js";
import { Chart } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Legend,
  Tooltip,
  ChartDataLabels
);

const IssueBarLineChart = ({ issueBarLineChart }) => {
  // --- Input Validation ---
  if (!issueBarLineChart || !Array.isArray(issueBarLineChart) || issueBarLineChart.length === 0) {
    return (
      <div className="text-sm text-red-400 text-center mt-10">
        No Data Available for this ARN.
      </div>
    );
  }

  // Basic validation for the structure of items in the array
  const isValidDataStructure = issueBarLineChart.every(item =>
    typeof item === 'object' && item !== null && 'key_as_string' in item && 'status_wise_count' in item
  );

  if (!isValidDataStructure) {
    return (
      <div className="text-red-400 text-sm text-center mt-10">
        No Data Available for this ARN.
      </div>
    );
  }
  // --- End Input Validation ---

  const labels = issueBarLineChart.map((item) => {
    const date = new Date(item.key_as_string);
    const month = date.toLocaleString("default", { month: "short" });
    const year = String(date.getFullYear()).slice(-2);
    return `${month}-${year}`;
  });

  const getCountsByStatus = (status) =>
    issueBarLineChart.map((item) => {
      const bucket = item.status_wise_count?.buckets?.find(
        (b) => b.key === status
      );
      return bucket ? bucket.doc_count : 0;
    });

  const open = getCountsByStatus("Open");
  const delayed = getCountsByStatus("Delayed");
  const onTrack = getCountsByStatus("On Track");
  const pending = getCountsByStatus("Pending");
  const closed = getCountsByStatus("Closed");

  // Filter out months where all 5 statuses are zero
  const filteredIndexes = open.map(
    (_, i) =>
      !(
        open[i] === 0 &&
        delayed[i] === 0 &&
        onTrack[i] === 0 &&
        pending[i] === 0 &&
        closed[i] === 0
      )
  );

  const filteredLabels = labels.filter((_, i) => filteredIndexes[i]);
  const filteredOpen = open.filter((_, i) => filteredIndexes[i]);
  const filteredDelayed = delayed.filter((_, i) => filteredIndexes[i]);
  const filteredOnTrack = onTrack.filter((_, i) => filteredIndexes[i]);
  const filteredPending = pending.filter((_, i) => filteredIndexes[i]);
  const filteredClosed = closed.filter((_, i) => filteredIndexes[i]);

  // Check if any data exists in filtered arrays
  const hasData =
    filteredOpen.length > 0 &&
    (filteredOpen.some((val) => val > 0) ||
      filteredDelayed.some((val) => val > 0) ||
      filteredOnTrack.some((val) => val > 0) ||
      filteredPending.some((val) => val > 0) ||
      filteredClosed.some((val) => val > 0));

  if (!hasData) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "2rem",
          fontSize: "1.2rem",
          color: "#666",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        No data found after filtering.
      </div>
    );
  }

  const totalRaised = filteredOpen.map(
    (val, i) =>
      val +
      filteredDelayed[i] +
      filteredOnTrack[i] +
      filteredPending[i] +
      filteredClosed[i]
  );

  // Shift line points a bit left by subtracting 0.2 from the index
  const lineData = totalRaised.map((y, i) => ({
    x: i - 0.2,
    y,
  }));

  const data = {
    labels: filteredLabels,
    datasets: [
      {
        label: "Open",
        data: filteredOpen,
        backgroundColor: "#a44b49",
        barPercentage: 1,
        categoryPercentage: 0.7,
      },
      {
        label: "Delayed",
        data: filteredDelayed,
        backgroundColor: "#2f5597",
        barPercentage: 1,
        categoryPercentage: 0.7,
      },
      {
        label: "On Track",
        data: filteredOnTrack,
        backgroundColor: "#f4b400",
        barPercentage: 1,
        categoryPercentage: 0.7,
      },
      {
        label: "Pending",
        data: filteredPending,
        backgroundColor: "#FF7900",
        barPercentage: 1,
        categoryPercentage: 0.7,
      },
      {
        label: "Closed",
        data: filteredClosed,
        backgroundColor: "#4f6228",
        barPercentage: 1,
        categoryPercentage: 0.7,
      },
      {
        type: "line",
        label: "Total Raised Issue",
        data: lineData,
        borderColor: "#0d6abf",
        borderWidth: 2,
        borderDash: [5, 5],
        fill: false,
        pointRadius: 0, // Hide dots
        pointBackgroundColor: "#0d6abf",
        parsing: false,
        datalabels: {
          display: true,
          backgroundColor: "#0d6abf",
          borderRadius: 4,
          color: "#fff",
          font: {
            weight: "bold",
            size: 10,
          },
          anchor: "end",
          align: "top",
          offset: 10, // <--- Added margin bottom to line chart labels
          formatter: (val) => val.y ?? val,
        },
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 32,
        left: 0,
        right: 0,
        bottom: 0,
      },
    },
    plugins: {
      legend: {
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
        },
      },
      datalabels: {
        display: (context) => context.dataset.type !== "line",
        color: "#000",
        font: {
          weight: "bold",
        },
        anchor: "end",
        align: "top",
        formatter: (value) => {
          const val = value.y ?? value;
          return val === 0 ? null : val;
        },
      },
      tooltip: {
        mode: "index",
        intersect: false,
      },
    },
    scales: {
      x: {
        stacked: false,
        grid: {
          display: false,
        },
        ticks: {
          callback: (val, index) => filteredLabels[index],
        },
      },
      y: {
        display: false,
        beginAtZero: true,
        max: 50,
        grid: {
          display: false,
        },
      },
    },

  };

  return (
    <div style={{ height: "calc(100% - 60px)" }}>
      <Chart type="bar" data={data} options={options} plugins={[ChartDataLabels]} />
    </div>
  );
};

export default IssueBarLineChart;